"use client";
import { useTranslations } from "@i18n/react";
import type { Images, UseMutationResult } from "@packages/sdk";
import {
  useRedirectToAuth,
  useRequestCollectionFavorite,
  useRequestFavorite,
  useRequestMe,
  useSession,
} from "@packages/sdk";
import { useAlert } from "@packages/ui";

import { useRedirectToSubscribe } from "./useRedirectToSubscribe";

export type UseFavoriteCollectionActionProps = {
  id: number;
} & Pick<UseFavoriteActionProps, "has_access" | "title" | "images">;

export const useFavoriteCollectionAction = ({
  id,
  has_access,
  title,
  images,
}: UseFavoriteCollectionActionProps) => {
  const { mutationPost: favoriteCollection } = useRequestCollectionFavorite({
    id,
  });
  const action = useFavoriteAction({
    has_access,
    updateFavorite: favoriteCollection,
    title,
    images,
  });
  return {
    action,
    isPending: favoriteCollection.isPending,
  };
};

export type UseFavoritePrayerActionProps = {
  id: number;
} & Pick<UseFavoriteActionProps, "has_access" | "title" | "images">;

export const useFavoritePrayerAction = ({
  id,
  has_access,
  title,
  images,
}: UseFavoritePrayerActionProps) => {
  const { mutationPost: favoritePrayer } = useRequestFavorite({ id });
  return useFavoriteAction({
    has_access,
    updateFavorite: favoritePrayer,
    title,
    images,
  });
};

type UseFavoriteActionProps = {
  has_access: boolean;
  updateFavorite: UseMutationResult<unknown, Error, void, unknown>;
  title: string;
  images: Images;
};

const useFavoriteAction = ({
  has_access,
  updateFavorite,
  title,
  images,
}: UseFavoriteActionProps) => {
  const { status } = useSession();
  const { query: user } = useRequestMe();
  const redirectToSubscribe = useRedirectToSubscribe();
  const redirectToAuth = useRedirectToAuth();
  const alert = useAlert();
  const t = useTranslations();

  return async () => {
    if (status === "authenticated" && (user?.data?.subscription || has_access))
      try {
        await updateFavorite.mutateAsync();
      } catch (error) {
        console.error("error favoriting", error);
        alert.show({
          title: t("general_word_error"),
          description: error.message,
        });
      }
    else if (status === "authenticated")
      redirectToSubscribe({
        redirectBackOnSubscription: true,
        forContent: !!title && !!images ? { title, images } : null,
      });
    else redirectToAuth();
  };
};
